import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import type { Student, PracticeSession, Drill } from '../types';
import { Plus, Edit, Trash2, User, Calendar, Target, BookOpen, Clock, ChevronDown, ChevronUp, X, Dumbbell } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';

export default function StudentManagement() {
  const { t } = useTranslation();
  const user = useStore((state) => state.user);
  const [students, setStudents] = useState<Student[]>([]);
  const [practiceSessions, setPracticeSessions] = useState<Record<string, PracticeSession[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showPracticeForm, setShowPracticeForm] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [expandedStudents, setExpandedStudents] = useState<Set<string>>(new Set());
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [editingPractice, setEditingPractice] = useState<PracticeSession | null>(null);
  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);
  const [practiceToDelete, setPracticeToDelete] = useState<PracticeSession | null>(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    age: '',
    level: '',
    team: '',
    strengths: '',
    areasToImprove: '',
    notes: ''
  });

  const [practiceFormData, setPracticeFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    duration: '',
    focusAreas: '',
    notes: '',
    progress: '',
    drills: [] as Drill[]
  });

  const [currentDrill, setCurrentDrill] = useState<Drill>({
    title: '',
    description: '',
    videoUrl: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        // Fetch students
        const studentsQuery = query(
          collection(db, 'students'),
          where('userId', '==', user.uid)
        );
        
        const studentsSnapshot = await getDocs(studentsQuery);
        const studentsData = studentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate(),
          updatedAt: doc.data().updatedAt.toDate()
        })) as Student[];

        setStudents(studentsData);

        // Fetch practice sessions for all students
        const practiceSessionsData: Record<string, PracticeSession[]> = {};
        
        for (const student of studentsData) {
          const practiceQuery = query(
            collection(db, 'practice_sessions'),
            where('studentId', '==', student.id),
            orderBy('date', 'desc')
          );
          
          const practiceSnapshot = await getDocs(practiceQuery);
          practiceSessionsData[student.id] = practiceSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            date: doc.data().date.toDate(),
            createdAt: doc.data().createdAt.toDate(),
            updatedAt: doc.data().updatedAt.toDate()
          })) as PracticeSession[];
        }

        setPracticeSessions(practiceSessionsData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(t('studentManagement.failedToLoadStudents'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      const studentData = {
        userId: user.uid,
        firstName: formData.firstName,
        lastName: formData.lastName,
        age: parseInt(formData.age, 10),
        level: formData.level,
        team: formData.team,
        strengths: formData.strengths.split(',').map(s => s.trim()).filter(s => s),
        areasToImprove: formData.areasToImprove.split(',').map(s => s.trim()).filter(s => s),
        notes: formData.notes,
        updatedAt: new Date()
      };

      if (editingStudent) {
        await updateDoc(doc(db, 'students', editingStudent.id), studentData);
      } else {
        await addDoc(collection(db, 'students'), {
          ...studentData,
          createdAt: new Date()
        });
      }

      // Refresh data
      await refreshData();
      setShowForm(false);
      setEditingStudent(null);
      resetForm();
    } catch (err) {
      console.error('Error saving student:', err);
      setError(t('studentManagement.failedToSaveStudent'));
    }
  };

  const handlePracticeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudentId) return;

    try {
      const practiceData = {
        studentId: selectedStudentId,
        date: new Date(practiceFormData.date),
        duration: parseInt(practiceFormData.duration, 10),
        focusAreas: practiceFormData.focusAreas.split(',').map(s => s.trim()).filter(s => s),
        drills: practiceFormData.drills,
        notes: practiceFormData.notes,
        progress: practiceFormData.progress,
        updatedAt: new Date()
      };

      if (editingPractice) {
        await updateDoc(doc(db, 'practice_sessions', editingPractice.id), practiceData);
      } else {
        await addDoc(collection(db, 'practice_sessions'), {
          ...practiceData,
          createdAt: new Date()
        });
      }

      // Refresh practice sessions for this student
      await refreshPracticeSessionsForStudent(selectedStudentId);
      setShowPracticeForm(false);
      setEditingPractice(null);
      resetPracticeForm();
    } catch (err) {
      console.error('Error saving practice session:', err);
      setError(t('studentManagement.failedToSavePractice'));
    }
  };

  const refreshData = async () => {
    if (!user) return;

    const studentsQuery = query(
      collection(db, 'students'),
      where('userId', '==', user.uid)
    );
    
    const studentsSnapshot = await getDocs(studentsQuery);
    const studentsData = studentsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate()
    })) as Student[];

    setStudents(studentsData);

    // Refresh practice sessions
    const practiceSessionsData: Record<string, PracticeSession[]> = {};
    
    for (const student of studentsData) {
      const practiceQuery = query(
        collection(db, 'practice_sessions'),
        where('studentId', '==', student.id),
        orderBy('date', 'desc')
      );
      
      const practiceSnapshot = await getDocs(practiceQuery);
      practiceSessionsData[student.id] = practiceSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date.toDate(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate()
      })) as PracticeSession[];
    }

    setPracticeSessions(practiceSessionsData);
  };

  const refreshPracticeSessionsForStudent = async (studentId: string) => {
    const practiceQuery = query(
      collection(db, 'practice_sessions'),
      where('studentId', '==', studentId),
      orderBy('date', 'desc')
    );
    
    const practiceSnapshot = await getDocs(practiceQuery);
    const practiceData = practiceSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date.toDate(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate()
    })) as PracticeSession[];

    setPracticeSessions(prev => ({
      ...prev,
      [studentId]: practiceData
    }));
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      age: '',
      level: '',
      team: '',
      strengths: '',
      areasToImprove: '',
      notes: ''
    });
  };

  const resetPracticeForm = () => {
    setPracticeFormData({
      date: new Date().toISOString().split('T')[0],
      duration: '',
      focusAreas: '',
      notes: '',
      progress: '',
      drills: []
    });
    setCurrentDrill({
      title: '',
      description: '',
      videoUrl: ''
    });
  };

  const handleEdit = (student: Student) => {
    setEditingStudent(student);
    setFormData({
      firstName: student.firstName,
      lastName: student.lastName,
      age: student.age.toString(),
      level: student.level,
      team: student.team,
      strengths: student.strengths.join(', '),
      areasToImprove: student.areasToImprove.join(', '),
      notes: student.notes
    });
    setShowForm(true);
  };

  const handleEditPractice = (practice: PracticeSession) => {
    setEditingPractice(practice);
    setPracticeFormData({
      date: practice.date.toISOString().split('T')[0],
      duration: practice.duration.toString(),
      focusAreas: practice.focusAreas.join(', '),
      notes: practice.notes,
      progress: practice.progress,
      drills: practice.drills
    });
    setSelectedStudentId(practice.studentId);
    setShowPracticeForm(true);
  };

  const handleDelete = async (studentId: string) => {
    try {
      // Delete all practice sessions for this student first
      const practiceQuery = query(
        collection(db, 'practice_sessions'),
        where('studentId', '==', studentId)
      );
      
      const practiceSnapshot = await getDocs(practiceQuery);
      const deletePromises = practiceSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Delete the student
      await deleteDoc(doc(db, 'students', studentId));
      
      await refreshData();
      setStudentToDelete(null);
    } catch (err) {
      console.error('Error deleting student:', err);
      setError(t('studentManagement.failedToDeleteStudent'));
    }
  };

  const handleDeletePractice = async (practiceId: string, studentId: string) => {
    try {
      await deleteDoc(doc(db, 'practice_sessions', practiceId));
      await refreshPracticeSessionsForStudent(studentId);
      setPracticeToDelete(null);
    } catch (err) {
      console.error('Error deleting practice session:', err);
      setError(t('studentManagement.failedToDeletePractice'));
    }
  };

  const toggleStudentExpansion = (studentId: string) => {
    setExpandedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const addDrill = () => {
    if (!currentDrill.title || !currentDrill.description) return;

    setPracticeFormData(prev => ({
      ...prev,
      drills: [...prev.drills, currentDrill]
    }));
    setCurrentDrill({
      title: '',
      description: '',
      videoUrl: ''
    });
  };

  const removeDrill = (index: number) => {
    setPracticeFormData(prev => ({
      ...prev,
      drills: prev.drills.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1b3968] mb-4"></div>
          <p className="text-gray-600">{t('studentManagement.loadingStudents')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-6xl mx-auto px-4 py-8'>
      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8'>
        <div>
          <h1 className='text-2xl md:text-3xl font-bold text-primary-600 italic'>
            {t('studentManagement.title')}
          </h1>
          <p className='text-gray-600 mt-2'>
            Manage your goalie students and track their practice sessions
          </p>
        </div>
        <button
          onClick={() => {
            setEditingStudent(null);
            resetForm();
            setShowForm(true);
          }}
          className='w-full sm:w-auto bg-[#1b3968] text-white px-4 py-2 rounded-md hover:bg-[#1b3968]/90 flex items-center justify-center gap-2'
        >
          <Plus className='h-5 w-5' />
          {t('studentManagement.addStudent')}
        </button>
      </div>

      {error && (
        <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'>
          {error}
        </div>
      )}

      {students.length === 0 ? (
        <div className='text-center py-12 bg-gray-50 rounded-lg'>
          <User className='h-16 w-16 text-gray-400 mx-auto mb-4' />
          <h3 className='text-lg font-medium text-gray-900 mb-2'>
            {t('studentManagement.noStudentsYet')}
          </h3>
          <p className='text-gray-600 mb-6'>
            {t('studentManagement.noStudentsDescription')}
          </p>
          <button
            onClick={() => setShowForm(true)}
            className='bg-[#1b3968] text-white px-4 py-2 rounded-md hover:bg-[#1b3968]/90 flex items-center gap-2 mx-auto'
          >
            <Plus className='h-5 w-5' />
            {t('studentManagement.addFirstStudent')}
          </button>
        </div>
      ) : (
        <div className='space-y-6'>
          {students.map((student) => {
            const isExpanded = expandedStudents.has(student.id);
            const studentPracticeSessions = practiceSessions[student.id] || [];

            return (
              <div key={student.id} className='bg-white shadow rounded-lg'>
                {/* Student Header */}
                <div className='p-6 border-b border-gray-200'>
                  <div className='flex justify-between items-start'>
                    <div className='flex-1'>
                      <div className='flex items-center gap-4'>
                        <button
                          onClick={() => toggleStudentExpansion(student.id)}
                          className='flex items-center gap-2 text-left'
                        >
                          {isExpanded ? (
                            <ChevronUp className='h-5 w-5 text-gray-400' />
                          ) : (
                            <ChevronDown className='h-5 w-5 text-gray-400' />
                          )}
                          <h3 className='text-xl font-semibold text-gray-900'>
                            {student.firstName} {student.lastName}
                          </h3>
                        </button>
                      </div>

                      <div className='mt-2 grid grid-cols-1 md:grid-cols-3 gap-4'>
                        <div className='flex items-center gap-2'>
                          <Target className='h-4 w-4 text-gray-400' />
                          <span className='text-sm text-gray-600'>
                            {student.team} • {student.level}
                          </span>
                        </div>
                        <div className='flex items-center gap-2'>
                          <Calendar className='h-4 w-4 text-gray-400' />
                          <span className='text-sm text-gray-600'>
                            Age: {student.age}
                          </span>
                        </div>
                        <div className='flex items-center gap-2'>
                          <BookOpen className='h-4 w-4 text-gray-400' />
                          <span className='text-sm text-gray-600'>
                            {studentPracticeSessions.length} practice sessions
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className='flex items-center space-x-2'>
                      <button
                        onClick={() => {
                          setSelectedStudentId(student.id);
                          setEditingPractice(null);
                          resetPracticeForm();
                          setShowPracticeForm(true);
                        }}
                        className='text-[#1b3968] hover:text-[#1b3968]/80 px-3 py-1.5 rounded-md hover:bg-gray-50 text-sm font-medium'
                      >
                        {t('studentManagement.addPractice')}
                      </button>
                      <button
                        onClick={() => handleEdit(student)}
                        className='text-gray-400 hover:text-[#1b3968] p-1'
                        title='Edit student'
                      >
                        <Edit className='h-4 w-4' />
                      </button>
                      <button
                        onClick={() => setStudentToDelete(student)}
                        className='text-gray-400 hover:text-red-500 p-1'
                        title='Delete student'
                      >
                        <Trash2 className='h-4 w-4' />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className='p-6'>
                    {/* Student Details */}
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
                      {student.strengths.length > 0 && (
                        <div>
                          <h4 className='text-sm font-medium text-gray-700 mb-2'>
                            Strengths
                          </h4>
                          <div className='flex flex-wrap gap-1'>
                            {student.strengths.map((strength, index) => (
                              <span
                                key={index}
                                className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800'
                              >
                                {strength}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {student.areasToImprove.length > 0 && (
                        <div>
                          <h4 className='text-sm font-medium text-gray-700 mb-2'>
                            Areas to Improve
                          </h4>
                          <div className='flex flex-wrap gap-1'>
                            {student.areasToImprove.map((area, index) => (
                              <span
                                key={index}
                                className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800'
                              >
                                {area}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {student.notes && (
                      <div className='mb-6'>
                        <h4 className='text-sm font-medium text-gray-700 mb-2'>
                          Notes
                        </h4>
                        <p className='text-sm text-gray-600 bg-gray-50 p-3 rounded-md'>
                          {student.notes}
                        </p>
                      </div>
                    )}

                    {/* Practice Sessions */}
                    <div>
                      <h4 className='text-lg font-medium text-gray-900 mb-4'>
                        {t('studentManagement.practiceSessions')}
                      </h4>
                      {studentPracticeSessions.length === 0 ? (
                        <div className='text-center py-8 bg-gray-50 rounded-lg'>
                          <Clock className='h-12 w-12 text-gray-400 mx-auto mb-3' />
                          <p className='text-gray-600 mb-4'>
                            {t('studentManagement.noPracticeSessionsYet')}
                          </p>
                          <button
                            onClick={() => {
                              setSelectedStudentId(student.id);
                              setEditingPractice(null);
                              resetPracticeForm();
                              setShowPracticeForm(true);
                            }}
                            className='bg-[#1b3968] text-white px-4 py-2 rounded-md hover:bg-[#1b3968]/90 flex items-center gap-2 mx-auto'
                          >
                            <Plus className='h-4 w-4' />
                            Add First Practice
                          </button>
                        </div>
                      ) : (
                        <div className='space-y-4'>
                          {studentPracticeSessions.map((practice) => (
                            <div
                              key={practice.id}
                              className='bg-gray-50 rounded-lg p-4 border border-gray-200'
                            >
                              <div className='flex justify-between items-start mb-3'>
                                <div>
                                  <div className='flex items-center gap-2 mb-1'>
                                    <Calendar className='h-4 w-4 text-gray-400' />
                                    <span className='font-medium'>
                                      {practice.date.toLocaleDateString()}
                                    </span>
                                    <Clock className='h-4 w-4 text-gray-400 ml-2' />
                                    <span className='text-sm text-gray-600'>
                                      {practice.duration} minutes
                                    </span>
                                  </div>
                                  {practice.focusAreas.length > 0 && (
                                    <div className='flex flex-wrap gap-1 mt-2'>
                                      {practice.focusAreas.map(
                                        (area, index) => (
                                          <span
                                            key={index}
                                            className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800'
                                          >
                                            {area}
                                          </span>
                                        )
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className='flex space-x-2'>
                                  <button
                                    onClick={() => handleEditPractice(practice)}
                                    className='text-gray-400 hover:text-[#1b3968]'
                                    title='Edit practice'
                                  >
                                    <Edit className='h-4 w-4' />
                                  </button>
                                  <button
                                    onClick={() =>
                                      setPracticeToDelete(practice)
                                    }
                                    className='text-gray-400 hover:text-red-500'
                                    title='Delete practice'
                                  >
                                    <Trash2 className='h-4 w-4' />
                                  </button>
                                </div>
                              </div>

                              {practice.drills.length > 0 && (
                                <div className='mb-3'>
                                  <h5 className='text-sm font-medium text-gray-700 mb-2'>
                                    Drills
                                  </h5>
                                  <div className='space-y-2'>
                                    {practice.drills.map((drill, index) => (
                                      <div
                                        key={index}
                                        className='bg-white p-3 rounded border'
                                      >
                                        <h6 className='font-medium text-sm'>
                                          {drill.title}
                                        </h6>
                                        <p className='text-xs text-gray-600 mt-1'>
                                          {drill.description}
                                        </p>
                                        {drill.videoUrl && (
                                          <a
                                            href={drill.videoUrl}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='text-[#1b3968] hover:text-[#1b3968]/80 text-xs mt-1 inline-block'
                                          >
                                            Watch Video
                                          </a>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {practice.notes && (
                                <div className='mb-3'>
                                  <h5 className='text-sm font-medium text-gray-700 mb-1'>
                                    Notes
                                  </h5>
                                  <p className='text-sm text-gray-600'>
                                    {practice.notes}
                                  </p>
                                </div>
                              )}

                              {practice.progress && (
                                <div>
                                  <h5 className='text-sm font-medium text-gray-700 mb-1'>
                                    Progress
                                  </h5>
                                  <p className='text-sm text-gray-600'>
                                    {practice.progress}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Student Form */}
      {showForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6'>
            <div className='flex justify-between items-center mb-6'>
              <h2 className='text-xl font-bold'>
                {editingStudent ? 'Edit Student' : 'Add New Student'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingStudent(null);
                }}
                className='text-gray-400 hover:text-gray-500'
              >
                <X className='h-6 w-6' />
              </button>
            </div>

            <form onSubmit={handleSubmit} className='space-y-6'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    First Name
                  </label>
                  <input
                    type='text'
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                    required
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Last Name
                  </label>
                  <input
                    type='text'
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                    required
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Age
                  </label>
                  <input
                    type='number'
                    value={formData.age}
                    onChange={(e) =>
                      setFormData({ ...formData, age: e.target.value })
                    }
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                    min='1'
                    required
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Level
                  </label>
                  <select
                    value={formData.level}
                    onChange={(e) =>
                      setFormData({ ...formData, level: e.target.value })
                    }
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                    required
                  >
                    <option value=''>{t('auth.selectLevel')}</option>
                    <option value='Recreational'>{t('auth.recreational')}</option>
                    <option value='Junior'>{t('auth.junior')}</option>
                    <option value='High School'>{t('auth.highSchool')}</option>
                    <option value='College'>{t('auth.college')}</option>
                    <option value='Semi-Professional'>{t('auth.semiProfessional')}</option>
                    <option value='Professional'>{t('auth.professional')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Team
                </label>
                <input
                  type='text'
                  value={formData.team}
                  onChange={(e) =>
                    setFormData({ ...formData, team: e.target.value })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                  required
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Strengths (comma-separated)
                </label>
                <input
                  type='text'
                  value={formData.strengths}
                  onChange={(e) =>
                    setFormData({ ...formData, strengths: e.target.value })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                  placeholder='ex: Réflexes rapides, Bon positionnement, Main forte'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Areas to Improve (comma-separated)
                </label>
                <input
                  type='text'
                  value={formData.areasToImprove}
                  onChange={(e) =>
                    setFormData({ ...formData, areasToImprove: e.target.value })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                  placeholder='ex: Mouvement latéral, Contrôle des retours, Communication'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                  rows={4}
                  placeholder="Notes supplémentaires sur l'étudiant..."
                />
              </div>

              <div className='flex justify-end space-x-3'>
                <button
                  type='button'
                  onClick={() => {
                    setShowForm(false);
                    setEditingStudent(null);
                  }}
                  className='px-4 py-2 text-gray-700 hover:text-gray-900'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='bg-[#1b3968] text-white px-4 py-2 rounded-md hover:bg-[#1b3968]/90'
                >
                  {editingStudent ? 'Update Student' : 'Add Student'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Practice Session Form */}
      {showPracticeForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6'>
            <div className='flex justify-between items-center mb-6'>
              <h2 className='text-xl font-bold'>
                {editingPractice
                  ? 'Edit Practice Session'
                  : 'Add New Practice Session'}
              </h2>
              <button
                onClick={() => {
                  setShowPracticeForm(false);
                  setEditingPractice(null);
                  setSelectedStudentId(null);
                }}
                className='text-gray-400 hover:text-gray-500'
              >
                <X className='h-6 w-6' />
              </button>
            </div>

            <form onSubmit={handlePracticeSubmit} className='space-y-6'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Date
                  </label>
                  <input
                    type='date'
                    value={practiceFormData.date}
                    onChange={(e) =>
                      setPracticeFormData({
                        ...practiceFormData,
                        date: e.target.value,
                      })
                    }
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                    required
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Duration (minutes)
                  </label>
                  <input
                    type='number'
                    value={practiceFormData.duration}
                    onChange={(e) =>
                      setPracticeFormData({
                        ...practiceFormData,
                        duration: e.target.value,
                      })
                    }
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                    min='1'
                    required
                  />
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Focus Areas (comma-separated)
                </label>
                <input
                  type='text'
                  value={practiceFormData.focusAreas}
                  onChange={(e) =>
                    setPracticeFormData({
                      ...practiceFormData,
                      focusAreas: e.target.value,
                    })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                  placeholder='ex: Technique papillon, Mouvement latéral, Contrôle des retours'
                />
              </div>

              {/* Drills Section */}
              <div>
                <div className='flex justify-between items-center mb-3'>
                  <label className='block text-sm font-medium text-gray-700'>
                    Drills
                  </label>
                </div>

                {practiceFormData.drills.length > 0 && (
                  <div className='space-y-2 mb-4'>
                    {practiceFormData.drills.map((drill, index) => (
                      <div
                        key={index}
                        className='bg-gray-50 p-3 rounded border flex justify-between items-start'
                      >
                        <div className='flex-1'>
                          <h4 className='font-medium text-sm'>{drill.title}</h4>
                          <p className='text-xs text-gray-600 mt-1'>
                            {drill.description}
                          </p>
                          {drill.videoUrl && (
                            <a
                              href={drill.videoUrl}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-[#1b3968] hover:text-[#1b3968]/80 text-xs mt-1 inline-block'
                            >
                              Watch Video
                            </a>
                          )}
                        </div>
                        <button
                          type='button'
                          onClick={() => removeDrill(index)}
                          className='text-gray-400 hover:text-red-500 ml-2'
                        >
                          <Trash2 className='h-4 w-4' />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className='bg-gray-50 p-4 rounded border space-y-3'>
                  <h4 className='font-medium text-sm flex items-center gap-2'>
                    <Dumbbell className='h-4 w-4' />
                    Add Drill
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                    <input
                      type='text'
                      value={currentDrill.title}
                      onChange={(e) =>
                        setCurrentDrill({
                          ...currentDrill,
                          title: e.target.value,
                        })
                      }
                      placeholder="Titre de l'exercice"
                      className='px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968] text-sm'
                    />
                    <input
                      type='url'
                      value={currentDrill.videoUrl}
                      onChange={(e) =>
                        setCurrentDrill({
                          ...currentDrill,
                          videoUrl: e.target.value,
                        })
                      }
                      placeholder='URL de la vidéo (optionnel)'
                      className='px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968] text-sm'
                    />
                  </div>
                  <textarea
                    value={currentDrill.description}
                    onChange={(e) =>
                      setCurrentDrill({
                        ...currentDrill,
                        description: e.target.value,
                      })
                    }
                    placeholder="Description de l'exercice"
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968] text-sm'
                    rows={2}
                  />
                  <button
                    type='button'
                    onClick={addDrill}
                    disabled={!currentDrill.title || !currentDrill.description}
                    className='bg-[#1b3968] text-white px-3 py-1.5 rounded-md hover:bg-[#1b3968]/90 disabled:opacity-50 text-sm'
                  >
                    Add Drill
                  </button>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Session Notes
                </label>
                <textarea
                  value={practiceFormData.notes}
                  onChange={(e) =>
                    setPracticeFormData({
                      ...practiceFormData,
                      notes: e.target.value,
                    })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                  rows={3}
                  placeholder="Notes sur la session d'entraînement..."
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Progress & Observations
                </label>
                <textarea
                  value={practiceFormData.progress}
                  onChange={(e) =>
                    setPracticeFormData({
                      ...practiceFormData,
                      progress: e.target.value,
                    })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                  rows={3}
                  placeholder="Progrès de l'étudiant et améliorations observées..."
                />
              </div>

              <div className='flex justify-end space-x-3'>
                <button
                  type='button'
                  onClick={() => {
                    setShowPracticeForm(false);
                    setEditingPractice(null);
                    setSelectedStudentId(null);
                  }}
                  className='px-4 py-2 text-gray-700 hover:text-gray-900'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='bg-[#1b3968] text-white px-4 py-2 rounded-md hover:bg-[#1b3968]/90'
                >
                  {editingPractice ? 'Update Practice' : 'Add Practice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Student Confirmation Modal */}
      {studentToDelete && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg max-w-md w-full p-6'>
            <h2 className='text-xl font-bold mb-4'>Delete Student</h2>
            <p className='text-gray-600 mb-6'>
              Are you sure you want to delete {studentToDelete.firstName}{' '}
              {studentToDelete.lastName}? This will also delete all their
              practice sessions. This action cannot be undone.
            </p>
            <div className='flex justify-end gap-4'>
              <button
                onClick={() => setStudentToDelete(null)}
                className='text-gray-600 hover:text-gray-900'
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(studentToDelete.id)}
                className='bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600'
              >
                Delete Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Practice Confirmation Modal */}
      {practiceToDelete && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg max-w-md w-full p-6'>
            <h2 className='text-xl font-bold mb-4'>Delete Practice Session</h2>
            <p className='text-gray-600 mb-6'>
              Are you sure you want to delete this practice session from{' '}
              {practiceToDelete.date.toLocaleDateString()}? This action cannot
              be undone.
            </p>
            <div className='flex justify-end gap-4'>
              <button
                onClick={() => setPracticeToDelete(null)}
                className='text-gray-600 hover:text-gray-900'
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handleDeletePractice(
                    practiceToDelete.id,
                    practiceToDelete.studentId
                  )
                }
                className='bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600'
              >
                Delete Practice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}